/**
 * Initialize FAQ/Accordion functionality for imported HTML content
 * Handles click events for FAQ items to toggle visibility
 */
export const initializeFAQAccordions = (container: HTMLElement | null) => {
  if (!container) return;

  // Find all FAQ items or accordion triggers
  const faqItems = container.querySelectorAll(
    '[class*="faq"], [class*="accordion"], [data-faq], .question, .faq-item'
  );

  faqItems.forEach(item => {
    // Check if item has a clickable question/title
    const question = item.querySelector('[class*="question"], [class*="title"], h3, h4');
    const answer = item.querySelector('[class*="answer"], [class*="content"], p:last-of-type');

    if (question && answer) {
      // Make question clickable if not already
      if (question.tagName !== 'BUTTON') {
        question.style.cursor = 'pointer';
        question.setAttribute('role', 'button');
        question.setAttribute('tabindex', '0');
      }

      // Hide answer initially or add toggle class
      const isHidden = answer.style.display === 'none' || 
                      answer.classList.contains('hidden') ||
                      answer.classList.contains('collapsed');

      // Add click handler
      const toggleAnswer = () => {
        const isCurrentlyHidden = answer.style.display === 'none' || 
                                 answer.classList.contains('hidden');
        
        if (isCurrentlyHidden) {
          answer.style.display = 'block';
          answer.classList.remove('hidden', 'collapsed');
          answer.classList.add('visible', 'expanded');
          question.classList.add('active');
        } else {
          answer.style.display = 'none';
          answer.classList.add('hidden', 'collapsed');
          answer.classList.remove('visible', 'expanded');
          question.classList.remove('active');
        }
      };

      // Attach event listeners
      question.addEventListener('click', toggleAnswer);
      
      // Handle keyboard navigation
      if (question.tagName !== 'BUTTON') {
        question.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleAnswer();
          }
        });
      }
    }
  });

  // Also handle FAQ items with data attributes
  const dataFaqItems = container.querySelectorAll('[data-faq-question]');
  dataFaqItems.forEach(item => {
    const answer = item.getAttribute('data-faq-answer');
    if (answer) {
      item.style.cursor = 'pointer';
      item.addEventListener('click', function() {
        const answerEl = this.nextElementSibling;
        if (answerEl) {
          const isHidden = (answerEl as HTMLElement).style.display === 'none';
          (answerEl as HTMLElement).style.display = isHidden ? 'block' : 'none';
          (this as HTMLElement).classList.toggle('active');
        }
      });
    }
  });

  // Handle details/summary HTML5 elements
  const summaryElements = container.querySelectorAll('summary');
  summaryElements.forEach(summary => {
    summary.style.cursor = 'pointer';
    summary.addEventListener('click', () => {
      const details = summary.parentElement;
      if (details && details.tagName === 'DETAILS') {
        details.classList.toggle('open');
      }
    });
  });
};

/**
 * Apply additional CSS to make FAQ items visually distinct and interactive
 */
export const applyFAQStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
    /* FAQ/Accordion styles for imported content */
    .imported-html-content [class*="faq"],
    .imported-html-content [class*="accordion"],
    .imported-html-content [data-faq] {
      margin-bottom: 1rem;
    }

    .imported-html-content [class*="question"],
    .imported-html-content [class*="title"]:has(+ [class*="answer"]) {
      cursor: pointer;
      padding: 1rem;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-weight: 600;
      user-select: none;
      transition: all 0.3s ease;
    }

    .imported-html-content [class*="question"]:hover,
    .imported-html-content [class*="question"].active,
    .imported-html-content [class*="question"][data-state="open"] {
      background: #f3f4f6;
      border-color: #d1d5db;
    }

    .imported-html-content [class*="answer"],
    .imported-html-content [class*="content"] {
      padding: 1rem;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-top: none;
      border-radius: 0 0 0.5rem 0.5rem;
      line-height: 1.6;
    }

    .imported-html-content details {
      margin-bottom: 1rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      overflow: hidden;
    }

    .imported-html-content summary {
      cursor: pointer;
      padding: 1rem;
      background: #f9fafb;
      font-weight: 600;
      user-select: none;
      transition: background 0.3s ease;
    }

    .imported-html-content summary:hover,
    .imported-html-content details[open] > summary {
      background: #f3f4f6;
    }

    .imported-html-content details[open] > summary {
      border-bottom: 1px solid #e5e7eb;
    }

    .imported-html-content details > *:not(summary) {
      padding: 1rem;
      background: #ffffff;
    }
  `;
  document.head.appendChild(style);
};
